import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { KeepoLoader } from "../../components/KeepoLoader";
import Modal, { ModalButton, ModalSecondaryButton } from "../../components/Modal";
import { PrimaryButton } from "../../components/Modal/styles";
import { isApiError } from "../../services/api/errors";
import { createKycLink, getPayments, KycLink, PaymentsState } from "../../services/payments";

const prefix = "pages.settings.payments.kycModal";
// Same as the app's bar: from here the sidebar shows (a computer); below it, a phone or tablet
const DESKTOP_QUERY = "(min-width: 1024px)";

// While open, the window asks how the review is going this often (a webhook updates it at once; else the API asks
// Pagar.me at most once a minute)
export const KYC_POLL_MS = 5_000;

const isDesktop = () => !!window.matchMedia?.(DESKTOP_QUERY).matches;

const clock = (milliseconds: number) => {
  const seconds = Math.ceil(milliseconds / 1000);
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
};

/**
 * The address that opens from here: only an https page (never another kind of address). One without a scheme is
 * https ("pagar.me/kyc/abc", "pagar.me:443/kyc/abc"); the scheme in any case ("HTTPS://") and spaces around are fine.
 * Anything else, or not an address at all, is none.
 */
const httpsAddress = (raw?: string | null): string | undefined => {
  const value = (raw ?? "").trim();
  if (!value) return undefined;
  // "host:port/path" has a colon too: a scheme is what is followed by "//", or one of the ones that run or save things
  const hasScheme =
    /^[a-z][a-z0-9+.-]*:\/\//i.test(value) || /^(javascript|data|vbscript|file|blob|mailto|tel):/i.test(value);
  const address = hasScheme ? value : `https://${value}`;
  try {
    return new URL(address).protocol === "https:" ? address : undefined;
  } catch {
    return undefined;
  }
};

/**
 * The identity check (prova de vida): on a computer the QR code takes the selfie to the phone; on a phone the
 * button opens the provider's page. The link lasts about 20 minutes, then a new one is generated.
 * While open it follows the review: the selfie sent (in analysis) shows Analisando; a decision (approved or refused)
 * goes to onDecided, with the Keepo's payments as they are now.
 */
export const KycModal = ({
  siteId,
  onClose,
  onDecided,
}: {
  siteId: string;
  onClose: () => void;
  onDecided: (state: PaymentsState) => void;
}) => {
  const { t } = useTranslation();
  const [link, setLink] = useState<KycLink>();
  const [analyzing, setAnalyzing] = useState(false);
  const decided = useRef(onDecided);
  decided.current = onDecided;
  const [error, setError] = useState<string>();
  const [now, setNow] = useState(() => Date.now());
  // Only the latest request counts, and none after closing
  const latest = useRef(0);

  const generate = useCallback(async () => {
    const request = ++latest.current;
    setLink(undefined);
    setError(undefined);
    try {
      const value = await createKycLink(siteId);
      if (request !== latest.current) {
        return;
      }
      const url = httpsAddress(value.url);
      if (!url) {
        setError("failed");
        return;
      }
      setNow(Date.now());
      setLink({ ...value, url });
    } catch (e) {
      if (request === latest.current) {
        setError(isApiError(e, "KYC_NOT_AVAILABLE") ? "notAvailable" : "failed");
      }
    }
  }, [siteId]);

  useEffect(() => {
    generate();
    const requests = latest;
    return () => {
      requests.current++;
    };
  }, [generate]);

  useEffect(() => {
    let open = true;
    const interval = setInterval(() => {
      getPayments(siteId)
        .then(state => {
          const status = state.recipient?.status;
          if (!open) return;
          if (status === "approved" || status === "refused") {
            open = false;
            clearInterval(interval);
            decided.current(state);
          } else if (status === "pending") {
            setAnalyzing(true);
          }
        })
        // a failed check is tried again on the next round
        .catch(() => undefined);
    }, KYC_POLL_MS);
    return () => {
      open = false;
      clearInterval(interval);
    };
  }, [siteId]);

  // An expiry that isn't a date counts as none
  const parsedExpiry = link?.expiresAt ? Date.parse(link.expiresAt) : NaN;
  const expiresAt = Number.isNaN(parsedExpiry) ? undefined : parsedExpiry;
  useEffect(() => {
    if (!expiresAt) {
      return undefined;
    }
    const interval = setInterval(() => {
      setNow(Date.now());
      if (Date.now() >= expiresAt) {
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  const remaining = expiresAt === undefined ? undefined : expiresAt - now;
  const isExpired = remaining !== undefined && remaining <= 0;
  const needsNewLink = isExpired || !!error;

  const body = () => {
    if (analyzing) {
      return (
        <Center role="status">
          <KeepoLoader size={24} color="var(--color-neutral-white)" />
          <Title>{t(`${prefix}.analyzing.title`)}</Title>
          <Hint>{t(`${prefix}.analyzing.hint`)}</Hint>
        </Center>
      );
    }
    if (error) {
      return null;
    }
    if (!link) {
      return (
        <Center role="status" aria-label={t(`${prefix}.loading`)}>
          <KeepoLoader size={24} color="var(--color-neutral-white)" />
        </Center>
      );
    }
    if (isExpired) {
      return <Hint>{t(`${prefix}.expired`)}</Hint>;
    }
    const showQr = !!link.qrCode && isDesktop();
    return (
      <Center>
        {showQr ? (
          <>
            <img src={link.qrCode as string} alt={t(`${prefix}.qrAlt`)} width={200} height={200} />
            <Hint>{t(`${prefix}.scan`)}</Hint>
          </>
        ) : (
          <>
            <Hint>{t(`${prefix}.openHint`)}</Hint>
            <PrimaryButton as="a" $variant="INFO" href={link.url} target="_blank" rel="noopener noreferrer">
              {t("pages.settings.payments.kyc")}
            </PrimaryButton>
          </>
        )}
        {remaining !== undefined && <Hint>{t(`${prefix}.countdown`, { time: clock(remaining) })}</Hint>}
      </Center>
    );
  };

  const Regenerate = needsNewLink ? ModalButton : ModalSecondaryButton;

  return (
    <Modal
      title={t(`${prefix}.title`)}
      subtitle={t(`${prefix}.subtitle`)}
      outsideExit
      onExit={onClose}
      error={!analyzing && error ? t(`${prefix}.errors.${error}`) : undefined}
      footer={
        analyzing ? undefined : (
          <Regenerate type="button" onClick={generate}>
            {t(`${prefix}.regenerate`)}
          </Regenerate>
        )
      }>
      {body()}
    </Modal>
  );
};

const Center = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  text-align: center;

  img {
    /* white margin around the code: a reader needs it, and it doesn't stick to the rounded edge */
    padding: 10px;
    box-sizing: content-box;
    background-color: #fff;
    border-radius: 12px;
  }
`;

const Title = styled.p`
  font-family: Inter, sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-neutral-white);
`;

const Hint = styled.p`
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-neutral-150);
`;
